import { TaskProgress, LessonTask, LessonProgress, LessonResult, ProgressProvider, Http, Store } from "@jelly/lesson";
import { LmsCourseProgress, LmsLessonProgress, LmsTaskProgress } from "../interfaces/LmsCourseProgress";

interface ProgressStoreData {
  lesson: Record<string, LessonProgress>;
}

export interface ProgressApi {
  courses: LmsCourseProgress[];
}

export default class LmsProgressProvider implements ProgressProvider {
  private store: Store<ProgressStoreData>;
  private cachedProgress: Promise<ProgressApi> | null = null;

  constructor(private http: Http) {
    this.store = new Store("lesson_progress", {
      lesson: {},
    });
  }

  /**
   * Use localStorage
   */
  getLessonProgress(lessonId: string): Promise<LessonProgress> {
    const value = this.store.get().lesson[lessonId];

    return value ? Promise.resolve(value) : Promise.resolve({ isComplete: false });
  }

  /**
   * Use localStorage
   */
  setLessonProgress(lessonId: string, data: LessonProgress): Promise<void> {
    this.store.update((storeData) => {
      storeData.lesson[lessonId] = data;
      return storeData;
    });

    return Promise.resolve();
  }

  async getTaskProgress(taskId: string): Promise<TaskProgress> {
    const lessonTask = await this.getCachedLessonTask(taskId);

    return {
      isCorrect: lessonTask.isCorrect,
      isComplete: lessonTask.isComplete,
    };
  }

  setTaskProgress(taskId: string, data: TaskProgress): Promise<void> {
    // Call api and update cached
    this.cachedProgress = this.http
      .patch("api/v1/asknow/user/progress", {
        json: {
          taskId,
          isCorrect: data.isCorrect,
        },
      })
      .json<ProgressApi>();

    return Promise.resolve();
  }

  async getLessonTask(lessonId: string): Promise<LessonTask[]> {
    const lesson = await this.getCachedLesson(lessonId);

    return lesson.tasks.map((task) => ({
      task: task.id,
      progress: {
        isCorrect: task.isCorrect,
        isComplete: task.isComplete,
      },
    }));
  }

  /**
   * Use localStorage
   *
   * This method return all saved lessons, not only this which is attached to the course.
   * In current cases this is enough
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getCourseLessonResults(courseId: string): Promise<LessonResult[]> {
    const lessons = this.store.get().lesson;

    return Promise.resolve(
      Object.keys(lessons).map((key) => ({
        lesson: key,
        isComplete: lessons[key].isComplete,
      }))
    );
  }

  async getCoursesProgress(): Promise<LmsCourseProgress[]> {
    const progress = await this.getCachedProgress();

    return progress.courses;
  }

  async getCourseProgress(courseId: string): Promise<LmsCourseProgress> {
    const progress = await this.getCachedProgress();
    const result = progress.courses.find((course) => course.id === courseId);

    return result ? result : Promise.reject(new Error(`Course ${courseId} don't exist`));
  }

  private getCachedProgress(): Promise<ProgressApi> {
    if (!this.cachedProgress) {
      this.cachedProgress = this.http.get("api/v1/asknow/user/progress").json<ProgressApi>();
    }

    return this.cachedProgress;
  }

  private async getCachedLesson(lessonId: string): Promise<LmsLessonProgress> {
    const progress = await this.getCachedProgress();

    let result: LmsLessonProgress | null = null;

    progress.courses.forEach((course) => {
      course.lessons.forEach((lesson) => {
        if (lesson.id === lessonId) {
          result = lesson;
        }
      });
    });

    return result ? result : Promise.reject(new Error(`Lesson ${lessonId} don't exist`));
  }

  private async getCachedLessonTask(taskId: string): Promise<LmsTaskProgress> {
    const progress = await this.getCachedProgress();

    let result: LmsTaskProgress | null = null;

    progress.courses.forEach((course) => {
      course.lessons.forEach((lesson) => {
        lesson.tasks.forEach((task) => {
          if (task.id === taskId) {
            result = task;
          }
        });
      });
    });

    return result ? result : Promise.reject(new Error(`Task ${taskId} don't exist`));
  }
}
